import moment from 'moment-timezone';
import { Types } from 'mongoose';
import { getToday } from 'sonata-react-components';
import { GoalTimeFrameEnum, GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';

import { getPaletteColor, withFlightColors } from '@/helpers/ioSettings/flightColors.helper';

import type { Moment } from 'moment-timezone';
import type { DatePickerMarker, DatePickerOccupiedRange, DatePickerUIRangeProps } from 'sonata-react-components';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { LineItemSchedule } from '@/components/ioSettings/InsertionOrderForm/hooks/useIoLineItemSchedules';

/** A flight while it is edited: dates are the saved Date or a UTC `YYYY-MM-DD` string. */
export type FlightState = {
  _id: string;
  beginDate?: Date | string;
  color?: string;
  endDate?: Date | string;
  goal?: Partial<Pick<InsertionOrderFlight['goal'], 'target' | 'timeFrame' | 'type'>>;
  name?: string;
};

export type FlightsState = Map<string, FlightState>;

/** Flight timezone: flights are stored as UTC midnights. */
export const FLIGHT_TIMEZONE = 'UTC';
/** Short numeric date in the browser locale, as DatePickerUI's default trigger format (its helper isn't exported). */
const localeDateFormatter = new Intl.DateTimeFormat(undefined, {
  calendar: 'gregory',
  day: '2-digit',
  month: '2-digit',
  numberingSystem: 'latn',
  timeZone: FLIGHT_TIMEZONE,
  year: 'numeric'
});
export const formatFlightDate = (date: Moment): string => localeDateFormatter.format(date.toDate());
const STORED_DATE_FORMAT = 'YYYY-MM-DD';

export const generateInitialFlight = (index: number): FlightState => ({
  _id: new Types.ObjectId().toString(),
  color: getPaletteColor(index),
  goal: { timeFrame: GoalTimeFrameEnum.overall, type: GoalTypeEnum.budget }
});

/** The IO's flights with their colours; a new IO starts with one empty flight. */
export const getInitialState = (items: InsertionOrderFlight[]): FlightsState => {
  const flights = items.length
    ? withFlightColors(items).map(item => ({ ...item, _id: String(item._id) }) as FlightState)
    : [generateInitialFlight(0)];
  return new Map(flights.map(flight => [flight._id, flight]));
};

/** Date-only UTC moment of a stored flight/line-item date. */
export const toDateOnly = (date?: Date | string | null): Moment | null =>
  date ? moment.utc(date).startOf('day') : null;

export const toStoredDate = (date: Moment | null): string | undefined =>
  date ? date.format(STORED_DATE_FORMAT) : undefined;

/** Every date set (date-only UTC) and the total budget of the flights. */
export const getFlightsTotals = (flights: FlightState[]): { dates: Moment[]; totalBudget: number } => ({
  dates: flights
    .flatMap(flight => [toDateOnly(flight.beginDate), toDateOnly(flight.endDate)])
    .filter((date): date is Moment => Boolean(date)),
  totalBudget: flights.reduce((acc, flight) => acc + (flight.goal?.target || 0), 0)
});

/** `onChange` payload: every flight with UTC `YYYY-MM-DD` dates, as the API expects. */
export const toFlightsPayload = (state: FlightsState): InsertionOrderFlight[] =>
  Array.from(state.values()).map(flight => ({
    ...flight,
    beginDate: toStoredDate(toDateOnly(flight.beginDate)),
    endDate: toStoredDate(toDateOnly(flight.endDate))
  })) as unknown as InsertionOrderFlight[];

/** Every flight with dates as a calendar range in its own colour (START-only = one day). */
export const getFlightRanges = (
  flights: FlightState[],
  getFallbackLabel: (index: number) => string
): DatePickerOccupiedRange[] =>
  flights.flatMap((flight, index) => {
    const startDate = toDateOnly(flight.beginDate);
    // START-only flights block their START day, as the old overlap check did
    const endDate = toDateOnly(flight.endDate) ?? startDate;
    return startDate && endDate
      ? [
          {
            color: flight.color ?? getPaletteColor(index),
            endDate,
            id: flight._id,
            label: flight.name || getFallbackLabel(index),
            startDate
          }
        ]
      : [];
  });

/** Line items with both dates as calendar markers, coloured by their (stable) position. */
export const getLineItemMarkers = (lineItems: LineItemSchedule[]): DatePickerMarker[] =>
  lineItems.flatMap((lineItem, index) => {
    const startDate = toDateOnly(lineItem.beginDate);
    const endDate = toDateOnly(lineItem.endDate);
    return startDate && endDate
      ? [{ color: getPaletteColor(index), endDate, id: String(lineItem._id), label: lineItem.name, startDate }]
      : [];
  });

/** Published flight already started (START on/before today, UTC): its START can't move. */
export const hasFlightStarted = (ioIsPublished: boolean, startDate: Moment | null): boolean =>
  ioIsPublished && Boolean(startDate?.isSameOrBefore(getToday(FLIGHT_TIMEZONE), 'day'));

/** Same window as line items: an ended flight's END can still be extended until it ended this many days ago. */
const ENDED_EDITABLE_DAYS = 30;

/**
 * Draft: no past days. Published: START locked once started (today included) and no past days, so an ended flight
 * can still extend its END from today on (up to the next flight: other flights block). Ended more than
 * `ENDED_EDITABLE_DAYS` ago → read-only.
 */
export const getPastRules = (
  ioIsPublished: boolean,
  startDate: Moment | null,
  endDate: Moment | null
): Pick<DatePickerUIRangeProps, 'disablePast' | 'lockedStart' | 'lockPastEnd' | 'lockPastStart'> => {
  if (!ioIsPublished) {
    return { disablePast: true };
  }
  const lockedStart = hasFlightStarted(ioIsPublished, startDate);
  const editableSince = getToday(FLIGHT_TIMEZONE).subtract(ENDED_EDITABLE_DAYS, 'days');
  return endDate?.isBefore(editableSince, 'day')
    ? { lockPastEnd: true, lockPastStart: true, lockedStart }
    : { disablePast: true, lockPastStart: true, lockedStart };
};
